import React, { useState, useEffect } from 'react';
import { settingsErrorMessage } from 'utils/settingsErrors';
import MagicButton from 'components/MagicButton/MagicButton';
import { Link } from 'react-router';
import { SettingsCard, SettingsRow, SettingsSection } from '../SettingsRow/SettingsRow';
import { useGetMeQuery, useUpdateAnimatedBorderMutation, useUpdateBannerMutation } from 'store/services/user/meApi';
import { EliteUpsellPrompt } from 'components/EliteUpsellPrompt/EliteUpsellPrompt';
import { useGetProfileSettingsQuery, useUpdateProfileSettingsMutation, useGetUserSocialLinksQuery, useSaveSocialLinksMutation } from 'store/services/profile/profileApi';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import BorderColorPickerModal from 'components/BorderColorPickerModal/BorderColorPickerModal';
import { BannerPickerModal } from 'components/BannerPickerModal';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import AvatarBorderPreviewBanner from '../AvatarBorderPreviewBanner/AvatarBorderPreviewBanner';
import ToggleSwitch from 'components/ToggleSwitch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssetUrls } from 'config/assets';
import { toast } from 'react-toastify';
import { containsUrl } from 'utils/urlDetection';
import { detectPlatform, PLATFORM_ICON_MAP, PLATFORM_LABEL_MAP } from 'utils/socialPlatformDetect';
import '../../Settings.css';

/** Settings > Public Profile: URL, appearance, bio, social links, profile visibility. Moved verbatim out of Settings.tsx. */
const ProfileTab: React.FC = () => {
  const [isBorderColorModalOpen, setIsBorderColorModalOpen] = useState(false);
  const [isBannerPickerOpen, setIsBannerPickerOpen] = useState(false);
  const [updateBanner, { isLoading: isUpdatingBanner }] = useUpdateBannerMutation();
  const [hasSavedBorder, setHasSavedBorder] = useState(false);

  // API hooks
  const { data: userData } = useGetMeQuery();
  const [updateAnimatedBorder] = useUpdateAnimatedBorderMutation();
  const [showEliteProfileUpsell, setShowEliteProfileUpsell] = useState(false);
  const { data: profileSettings, isLoading: isLoadingProfileSettings } = useGetProfileSettingsQuery();
  const [updateProfileSettings, { isLoading: isUpdatingProfileSettings }] = useUpdateProfileSettingsMutation();
  const [bio, setBio] = useState('');
  const [bioChanged, setBioChanged] = useState(false);
  const [bioHasUrl, setBioHasUrl] = useState(false);

  // Social links state
  const { data: socialLinksData } = useGetUserSocialLinksQuery(userData?.username || '', { skip: !userData?.username });
  const [saveSocialLinks, { isLoading: isSavingLinks }] = useSaveSocialLinksMutation();
  const [socialRows, setSocialRows] = useState<Array<{ url: string; error: string }>>([{ url: '', error: '' }]);
  const [socialLinksChanged, setSocialLinksChanged] = useState(false);

  // Hydrate hasSavedBorder flag from localStorage once userId is known
  useEffect(() => {
    if (userData?.id != null) {
      const key = `avatarBorderPreview.hasSaved.${userData.id}`;
      setHasSavedBorder(localStorage.getItem(key) === 'true');
    }
  }, [userData?.id]);

  const handleResetBanner = async () => {
    try {
      await updateBanner({ scryfallId: null }).unwrap();
    } catch {
      toast.error('Failed to reset banner');
    }
  };

  // Load social links from API
  useEffect(() => {
    if (socialLinksData) {
      if (socialLinksData.links.length > 0) {
        setSocialRows(socialLinksData.links.map((l) => ({ url: l.url, error: '' })));
      } else {
        setSocialRows([{ url: '', error: '' }]);
      }
      setSocialLinksChanged(false);
    }
  }, [socialLinksData]);

  // Load bio from profile settings
  useEffect(() => {
    if (profileSettings?.bio !== undefined) {
      setBio(profileSettings.bio || '');
      setBioChanged(false);
    }
  }, [profileSettings?.bio]);

  const handleAnimatedBorderToggle = async (checked: boolean) => {
    try {
      await updateAnimatedBorder({ enabled: checked }).unwrap();
      toast.success(
        checked
          ? 'Animated avatar border enabled'
          : 'Animated avatar border disabled'
      );
    } catch (error: any) {
      console.error('Failed to update animated border setting:', error);
      toast.error('Failed to update setting. Please try again.');
    }
  };

  const handleBioChange = (value: string) => {
    setBio(value);
    setBioChanged(value !== (profileSettings?.bio || ''));
    setBioHasUrl(containsUrl(value));
  };

  const handleSaveBio = async () => {
    try {
      await updateProfileSettings({ bio: bio || null }).unwrap();
      toast.success('Bio updated successfully');
      setBioChanged(false);
    } catch (error: any) {
      console.error('Failed to update bio:', error);
      const message = settingsErrorMessage(error, 'Failed to update bio. Please try again.');
      toast.error(message);
    }
  };

  const validateSocialUrl = (url: string): string => {
    if (!url.trim()) return '';
    if (!url.startsWith('https://')) return 'URL must start with https://';
    try {
      new URL(url);
      return '';
    } catch {
      return 'Invalid URL format';
    }
  };

  const handleSocialUrlChange = (index: number, value: string) => {
    setSocialRows((prev) =>
      prev.map((row, i) => (i === index ? { url: value, error: validateSocialUrl(value) } : row))
    );
    setSocialLinksChanged(true);
  };

  const handleAddSocialRow = () => {
    if (socialRows.length < 5) {
      setSocialRows((prev) => [...prev, { url: '', error: '' }]);
      setSocialLinksChanged(true);
    }
  };

  const handleRemoveSocialRow = (index: number) => {
    setSocialRows((prev) => prev.filter((_, i) => i !== index));
    setSocialLinksChanged(true);
  };

  const handleSaveSocialLinks = async () => {
    // Validate all rows
    const validated = socialRows.map((row) => ({ ...row, error: validateSocialUrl(row.url) }));
    setSocialRows(validated);

    const nonEmpty = validated.filter((r) => r.url.trim());
    if (nonEmpty.some((r) => r.error)) return;

    // Non-Elite users: redirect to Elite page
    if (!userData?.isElite) {
      window.location.href = '/elite?source=social-links';
      return;
    }

    // Elite users: save links
    try {
      await saveSocialLinks({
        links: nonEmpty.map((r, i) => ({ url: r.url.trim(), displayOrder: i + 1 })),
      }).unwrap();
      toast.success('Social links updated!');
      setSocialLinksChanged(false);
    } catch {
      toast.error('Failed to save social links.');
    }
  };

  const copy = async (url: string, message: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(message);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <>
      <SettingsSection label="Profile URL">
        <SettingsCard>
          <SettingsRow
            title="Default URL"
            description={<span className="settings-profile-url-text">trademagic.gg/profile/u/{userData?.username}</span>}
            control={
              <button
                className="settings-profile-url-copy"
                onClick={() => copy(`https://trademagic.gg/profile/u/${userData?.username}`, 'Profile link copied!')}
              >
                Copy
              </button>
            }
          />
          {userData?.isElite && userData?.customSlug && (
            <SettingsRow
              title="Custom Elite URL"
              description={
                <span className="settings-profile-url-line">
                  <span className="settings-profile-url-text settings-profile-url-elite">trademagic.gg/u/{userData.customSlug}</span>
                  <a href="/elite" className="settings-profile-url-manage">Manage on Elite page</a>
                </span>
              }
              control={
                <button
                  className="settings-profile-url-copy"
                  onClick={() => copy(`https://trademagic.gg/u/${userData.customSlug}`, 'Custom URL copied!')}
                >
                  Copy
                </button>
              }
            />
          )}
        </SettingsCard>
        {!userData?.isElite && (
          <p className="settings-note">
            Want a custom profile URL? <a href="/elite?source=custom-slug" className="settings-inline-link">Upgrade to Elite</a>
          </p>
        )}
      </SettingsSection>

      <SettingsSection label="Appearance">
        <SettingsCard>
          <SettingsRow
            className="settings-row-item--with-avatar"
            title={
              <>
                Animated avatar border
                <img src={AssetUrls.icons.elite.badge} alt="Elite feature" className="settings-elite-lock-badge" />
              </>
            }
            description="Display an animated glowing border around your avatar across the platform."
            control={
              <>
                <AvatarWithFlag
                  avatarUrl={userData?.avatarUrl}
                  username={userData?.username || ''}
                  size={36}
                  isElite={true}
                  showAnimatedBorder={true}
                  borderColor1={userData?.borderColor1 || '#FFD700'}
                  borderColor2={userData?.borderColor2 || '#9B59B6'}
                />
                <button className="settings-customize-border-btn" onClick={() => setIsBorderColorModalOpen(true)}>
                  Preview
                </button>
                <ToggleSwitch
                  checked={userData?.isElite ? (userData?.showAnimatedBorder ?? true) : false}
                  onChange={userData?.isElite ? handleAnimatedBorderToggle : () => setShowEliteProfileUpsell(true)}
                  ariaLabel="Animated avatar border"
                />
              </>
            }
          />
          {/* Hover card banner (Asana 1219946182379030, Decision 6): three states. */}
          <SettingsRow
            className="settings-row-item--with-avatar"
            title={
              <>
                Hover card banner
                <img src={AssetUrls.icons.elite.badge} alt="Elite feature" className="settings-elite-lock-badge" />
              </>
            }
            description={
              userData?.isElite && userData.bannerUrl ? (
                <>
                  Card art shown at the top of your hover card. Using {userData.bannerCardName}
                  {' · '}
                  <button
                    type="button"
                    className="billing-cancel-link settings-banner-reset"
                    onClick={handleResetBanner}
                    disabled={isUpdatingBanner}
                  >
                    Reset
                  </button>
                </>
              ) : (
                'Card art shown at the top of your hover card.'
              )
            }
            control={
              <>
                {userData?.isElite && userData.bannerUrl ? (
                  <img className="settings-banner-thumb" src={userData.bannerUrl} alt="" data-testid="settings-banner-thumb" />
                ) : (
                  <div
                    className="settings-banner-thumb settings-banner-thumb--gradient"
                    style={{
                      background: `linear-gradient(135deg, ${userData?.borderColor1 || '#FFD700'}, ${userData?.borderColor2 || '#9B59B6'})`,
                    }}
                    data-testid="settings-banner-thumb"
                  />
                )}
                <button
                  type="button"
                  className="settings-customize-border-btn settings-customize-border-btn--primary"
                  onClick={userData?.isElite ? () => setIsBannerPickerOpen(true) : () => setShowEliteProfileUpsell(true)}
                >
                  {!userData?.isElite && <FontAwesomeIcon icon={faLock} className="settings-banner-lock" />}
                  {userData?.isElite && userData.bannerUrl ? 'Change' : 'Choose art'}
                </button>
              </>
            }
          />
        </SettingsCard>
        <AvatarBorderPreviewBanner userId={userData?.id} visible={!userData?.isElite && hasSavedBorder} />
      </SettingsSection>

      <SettingsSection label="Bio">
        <SettingsCard>
          <SettingsRow
            stacked
            title="About you"
            description="Tell other traders about yourself. This will be displayed on your public profile."
            control={
              <div className="settings-freetext">
                <textarea
                  className="settings-bio-textarea"
                  placeholder="Tell other traders about yourself..."
                  value={bio}
                  onChange={(e) => handleBioChange(e.target.value)}
                  maxLength={500}
                  rows={4}
                  aria-label="Bio"
                />
                <CharacterCounter currentLength={bio.length} maxLength={500} warningThreshold={450} />
                {bioHasUrl && !userData?.isElite && (
                  <div className="settings-bio-url-warning">
                    Want to share links on your profile?{' '}
                    <a href="/elite?source=bio-url">Elite members</a> can add up to 5 social profile links with auto-detected platform icons.
                  </div>
                )}
                <div className="settings-freetext__save">
                  <MagicButton
                    label={isUpdatingProfileSettings ? "Saving..." : "Save Bio"}
                    color="purple"
                    onPress={handleSaveBio}
                    isDisabled={!bioChanged || isUpdatingProfileSettings || isLoadingProfileSettings || (bioHasUrl && !userData?.isElite)}
                  />
                </div>
              </div>
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Social links">
        <SettingsCard>
          <SettingsRow
            stacked
            title={
              <>
                Links to your social profiles
                <img src={AssetUrls.icons.elite.badge} alt="Elite" className="settings-elite-lock-badge" />
              </>
            }
            description="Add up to 5 links. Platform icons are auto-detected."
            control={
              <div className="settings-freetext">
                <div className="settings-social-links-list">
                  {socialRows.map((row, index) => {
                    const platform = row.url.trim() ? detectPlatform(row.url) : null;
                    const iconTuple = platform ? PLATFORM_ICON_MAP[platform] : null;
                    const label = platform ? PLATFORM_LABEL_MAP[platform] : null;
                    return (
                      <div key={index} className="settings-social-link-row">
                        <div className="settings-social-link-icon">
                          {iconTuple ? (
                            <FontAwesomeIcon icon={iconTuple} title={label || undefined} />
                          ) : (
                            <FontAwesomeIcon icon={['fas', 'link']} className="settings-social-link-icon--empty" />
                          )}
                        </div>
                        <div className="settings-social-link-input-wrapper">
                          <input
                            type="text"
                            className={`settings-social-link-input${row.error ? ' settings-social-link-input--error' : ''}`}
                            value={row.url}
                            onChange={(e) => handleSocialUrlChange(index, e.target.value)}
                            placeholder="https://twitter.com/yourname"
                          />
                          {row.error && <span className="settings-social-link-error">{row.error}</span>}
                        </div>
                        <button
                          className="settings-social-link-remove"
                          onClick={() => handleRemoveSocialRow(index)}
                          aria-label="Remove link"
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
                {socialRows.length < 5 && (
                  <button className="settings-social-link-add" onClick={handleAddSocialRow}>
                    + Add Link
                  </button>
                )}
                <div className="settings-freetext__save">
                  <MagicButton
                    label={isSavingLinks ? "Saving..." : "Save Links"}
                    color="purple"
                    onPress={handleSaveSocialLinks}
                    isDisabled={isSavingLinks || !socialLinksChanged}
                  />
                </div>
              </div>
            }
          />
        </SettingsCard>
      </SettingsSection>

      <p className="settings-note" data-testid="profile-privacy-pointer">
        The Have List and Contact button settings are now in{' '}
        <Link to="/settings/privacy" className="settings-inline-link">Privacy</Link>.
      </p>

      <EliteUpsellPrompt
        isOpen={showEliteProfileUpsell}
        onClose={() => setShowEliteProfileUpsell(false)}
        source="profile-customization"
        title="Profile Customization is an Elite Feature"
        body="Make your profile yours — animated avatar borders, custom gradient colors, and more."
        emojiRow={
          <>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#10024;</span> 3
            </span>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#127912;</span> 2
            </span>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#128142;</span> 1
            </span>
          </>
        }
        learnMoreLabel="Upgrade to Elite"
      />

      <BannerPickerModal
        isOpen={isBannerPickerOpen}
        onClose={() => setIsBannerPickerOpen(false)}
        username={userData?.username || ''}
        currentBannerUrl={userData?.bannerUrl ?? null}
      />

      <BorderColorPickerModal
        isOpen={isBorderColorModalOpen}
        onClose={() => setIsBorderColorModalOpen(false)}
        currentColor1={userData?.borderColor1 || '#FFD700'}
        currentColor2={userData?.borderColor2 || '#9B59B6'}
        avatarUrl={userData?.avatarUrl}
        username={userData?.username}
        onAfterSave={() => {
          if (!userData?.isElite && userData?.id != null) {
            localStorage.setItem(`avatarBorderPreview.hasSaved.${userData.id}`, 'true');
            setHasSavedBorder(true);
          }
        }}
      />
    </>
  );
};

export default ProfileTab;
